import { useT } from "@transifex/react";
import { useState } from "react";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import { ButtonColorVariant, IconPosition } from "../../teacherArea/AreaButton";
import { FormattedText } from "../../teacherArea/FormattedText";

interface DeleteClassMemberPopupProps {
    isOpen: boolean;
    classMemberNames: string[];
    onClose: () => void;
    onConfirm: () => Promise<void>;
}

const DeleteClassMemberPopup = (props: DeleteClassMemberPopupProps) => {
    const t = useT();

    const isSingle = props.classMemberNames.length === 1;
    const studentName = props.classMemberNames[0] ?? "";
    const count = props.classMemberNames.length;

    const t_title = isSingle
        ? t("Remove student", { _tags: "SchoolClasses, DeleteClassMemberPopup" })
        : t("Remove students", { _tags: "SchoolClasses, DeleteClassMemberPopup" });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, DeleteClassMemberPopup" });
    const t_delete = t("Delete", { _tags: "SchoolClasses, DeleteClassMemberPopup" });
    const t_confirmDelete = isSingle
        ? t("Are you sure you want to remove <b>{studentName}</b>?", {
              studentName,
              _tags: "SchoolClasses, DeleteClassMemberPopup",
          })
        : t("Are you sure you want to remove <b>{count} students</b>?", {
              count,
              _tags: "SchoolClasses, DeleteClassMemberPopup",
          });
    const t_accessWarning = isSingle
        ? t("{studentName} will lose access to the class, activities and results.", {
              studentName,
              _tags: "SchoolClasses, DeleteClassMemberPopup",
          })
        : t("They will lose access to the class, activities and results.", {
              _tags: "SchoolClasses, DeleteClassMemberPopup",
          });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await props.onConfirm();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={isSubmitting ? () => {} : props.onClose}
            title={t_title}
            titleColor={TitleColorVariants.DANGER}
            titleIcon="alert"
            secondaryButton={{
                text: t_cancel,
                onClick: props.onClose,
                disabled: isSubmitting,
                className: "min-w-[120px]",
            }}
            primaryButton={{
                text: t_delete,
                onClick: handleConfirm,
                disabled: isSubmitting,
                colorVariant: ButtonColorVariant.DANGER,
                icon: "trash",
                iconPosition: IconPosition.RIGHT,
                className: "min-w-[120px]",
            }}
        >
            <div className="flex flex-col gap-s">
                <FormattedText
                    rawText={t_confirmDelete}
                    textColor="text-l text-p6-grey-dark"
                />
                <p className="text-l text-p6-grey-dark">{t_accessWarning}</p>
            </div>
        </BasePopupWrapper>
    );
};

export default DeleteClassMemberPopup;
